// Cuentas del editor de polígonos de zona, sin Leaflet (se prueban solas).
//
// Un anillo es una lista de vértices [lat, lng] sin repetir el primero, igual
// que lo guarda el servidor (server/current/zones/polygon.js).
//
// El imán: al poner o arrastrar un vértice cerca de otro polígono, se pega a
// un vértice suyo (copia exacta) o al lado más cercano. Así dos barrios
// vecinos comparten el borde y el servidor no los toma como superpuestos.

export type LatLng = [number, number];
export type Ring = LatLng[];
export type Px = { x: number; y: number };
export type SnapVertex = Px & { lat: number; lng: number };

export const SNAP_PX = 12;
export const MIN_VERTICES = 3;

// Vértice al que pegar, o null si no hay nada cerca. Primero los vértices
// (se copia el valor exacto), después los lados: el punto sale de la misma
// fracción del lado en lat/lng, que es la recta que usa el servidor.
export function snapTo(p: Px, vertices: SnapVertex[], segments: [SnapVertex, SnapVertex][], radius = SNAP_PX): LatLng | null {
  let best: SnapVertex | null = null;
  let bestD = radius;
  for (const v of vertices) {
    const d = Math.hypot(v.x - p.x, v.y - p.y);
    if (d <= bestD) { best = v; bestD = d; }
  }
  if (best) return [best.lat, best.lng];

  let seg: LatLng | null = null;
  let segD = radius;
  for (const [a, b] of segments) {
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const len2 = dx * dx + dy * dy;
    if (!len2) continue;
    const t = Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2));
    const d = Math.hypot(a.x + t * dx - p.x, a.y + t * dy - p.y);
    if (d <= segD) {
      segD = d;
      seg = [a.lat + t * (b.lat - a.lat), a.lng + t * (b.lng - a.lng)];
    }
  }
  return seg;
}

// Lados de un anillo como pares de vértices proyectados.
export function ringSegments(ring: SnapVertex[]): [SnapVertex, SnapVertex][] {
  return ring.map((v, i) => [v, ring[(i + 1) % ring.length]] as [SnapVertex, SnapVertex]);
}

export function moveVertex(ring: Ring, index: number, to: LatLng): Ring {
  return ring.map((v, i) => (i === index ? to : v));
}

// Agrega un vértice en el medio del lado que empieza en `index`.
export function insertMidpoint(ring: Ring, index: number): Ring {
  const a = ring[index];
  const b = ring[(index + 1) % ring.length];
  const mid: LatLng = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
  return [...ring.slice(0, index + 1), mid, ...ring.slice(index + 1)];
}

// Saca un vértice; un polígono no baja de 3.
export function removeVertex(ring: Ring, index: number): Ring {
  if (ring.length <= MIN_VERTICES) return ring;
  return ring.filter((_, i) => i !== index);
}

export function midpoints(ring: Ring): LatLng[] {
  return ring.map((a, i) => {
    const b = ring[(i + 1) % ring.length];
    return [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2] as LatLng;
  });
}

// Caja que abarca todo lo que se pasa (para encuadrar el mapa), o null. De
// los puntos sueltos (entregas) se descarta el 5 % más alejado de cada lado:
// un par de envíos a otra provincia no deben alejar el mapa de la ciudad.
export function boundsOf(rings: Ring[], points: { lat: number; lng: number }[] = []): [LatLng, LatLng] | null {
  const all: LatLng[] = [...rings.flat(), ...trimmed(points)];
  if (!all.length) return null;
  let [minLat, minLng] = all[0];
  let [maxLat, maxLng] = all[0];
  for (const [lat, lng] of all) {
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
    if (lng < minLng) minLng = lng;
    if (lng > maxLng) maxLng = lng;
  }
  return [[minLat, minLng], [maxLat, maxLng]];
}

function trimmed(points: { lat: number; lng: number }[]): LatLng[] {
  if (points.length < 20) return points.map((p) => [p.lat, p.lng] as LatLng);
  const cut = Math.floor(points.length * 0.05);
  const lats = points.map((p) => p.lat).sort((a, b) => a - b).slice(cut, points.length - cut);
  const lngs = points.map((p) => p.lng).sort((a, b) => a - b).slice(cut, points.length - cut);
  return [[lats[0], lngs[0]], [lats[lats.length - 1], lngs[lngs.length - 1]]];
}
